import React from 'react';
import { Eye, Edit, Copy, Trash2, ChevronLeft, ChevronRight, FileText } from 'lucide-react';
import EmptyState from '../Common/EmptyState';
import Can from '../Common/Can';
import { P } from '../../constants/permissions';

const thClass =
  'px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 border-b border-gray-200 bg-gray-50';
const tdClass = 'px-3 py-3 text-sm text-gray-900 border-b border-gray-100 align-top';

const LicenseTable = ({
  licenses,
  pagination,
  totalCount,
  onView,
  onEdit,
  onDelete,
  onDuplicate,
  getStatusBadge,
  onCreate,
}) => {
  const total = totalCount != null ? totalCount : licenses?.length ?? 0;
  if (!licenses?.length) {
    return (
      <div className="card">
        <div className="card-content py-2">
          <EmptyState
            icon={FileText}
            title="Nenhuma licença encontrada"
            description="Ajuste os filtros de busca ou cadastre uma nova licença."
            action={
              onCreate ? (
                <Can permission={P.LICENSES_PAGE}>
                  <button type="button" className="btn btn-primary" onClick={onCreate}>
                    Nova licença
                  </button>
                </Can>
              ) : null
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="-mx-5 overflow-x-auto sm:-mx-6">
        <table className="w-full min-w-[72rem] border-collapse">
          <thead>
            <tr>
              <th className={`${thClass} w-10`}>
                <input type="checkbox" className="rounded border-gray-300" aria-label="Selecionar todas" />
              </th>
              <th className={`${thClass} whitespace-nowrap`}>Número</th>
              <th className={`${thClass} min-w-[16rem]`}>Empreendimento</th>
              <th className={`${thClass} min-w-[14rem]`}>Tipo</th>
              <th className={`${thClass} whitespace-nowrap`}>Status</th>
              <th className={`${thClass} whitespace-nowrap`}>Vencimento</th>
              <th className={`${thClass} min-w-[12rem]`}>Responsável</th>
              <th className={`${thClass} sticky right-0 z-20 whitespace-nowrap shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.18)]`}>
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {licenses.map((license) => (
              <tr key={license.id} className="group hover:bg-gray-50">
                <td className={tdClass}>
                  <input type="checkbox" className="rounded border-gray-300" aria-label={`Selecionar ${license.numero}`} />
                </td>
                <td className={`${tdClass} whitespace-nowrap`}>
                  <div className="font-medium text-gray-900">
                    {license.numero && license.numero !== '—' ? license.numero : '—'}
                  </div>
                  <div className="text-xs font-mono text-gray-500" title={license.id}>
                    {license.id ? `${String(license.id).slice(0, 10)}…` : ''}
                  </div>
                </td>
                <td className={`${tdClass} min-w-[16rem] max-w-xs whitespace-normal break-normal`}>
                  {license.enterprise}
                </td>
                <td className={`${tdClass} min-w-[14rem] max-w-xs whitespace-normal break-normal`}>
                  {license.type}
                </td>
                <td className={`${tdClass} whitespace-nowrap`}>{getStatusBadge(license.status)}</td>
                <td className={`${tdClass} whitespace-nowrap`}>
                  {license.expirationDate
                    ? new Date(license.expirationDate).toLocaleDateString('pt-BR')
                    : '—'}
                </td>
                <td className={`${tdClass} min-w-[12rem] max-w-xs whitespace-normal break-normal`}>{license.responsible}</td>
                <td
                  className={`${tdClass} sticky right-0 z-10 whitespace-nowrap bg-white shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.18)] group-hover:bg-gray-50`}
                >
                  <Can permission={P.LICENSES_PAGE}>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => onView(license)}
                        className="btn btn-outline btn-sm btn-icon"
                        title="Visualizar"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {onEdit ? (
                      <button
                        type="button"
                        onClick={() => onEdit(license)}
                        className="btn btn-outline btn-sm btn-icon"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      ) : null}
                      {onDuplicate ? (
                      <button
                        type="button"
                        onClick={() => onDuplicate(license)}
                        className="btn btn-outline btn-sm btn-icon"
                        title="Duplicar"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      ) : null}
                      {onDelete ? (
                      <button
                        type="button"
                        onClick={() => onDelete(license)}
                        className="btn btn-outline btn-sm btn-icon text-danger-600 hover:bg-danger-50 hover:text-danger-700"
                        title="Excluir"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      ) : null}
                    </div>
                  </Can>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination border-t border-gray-200 px-1 py-4 sm:px-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-gray-600">
            Mostrando {((pagination.page - 1) * pagination.limit) + 1} a{' '}
            {Math.min(pagination.page * pagination.limit, total)} de {total} licenças
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={pagination.prevPage}
              disabled={!pagination.hasPrev}
              className="pagination-btn"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
              const page = i + 1;
              return (
                <button
                  key={page}
                  type="button"
                  onClick={() => pagination.goToPage(page)}
                  className={`pagination-btn ${pagination.page === page ? 'active' : ''}`}
                >
                  {page}
                </button>
              );
            })}

            {pagination.totalPages > 5 && (
              <>
                <span className="px-2 text-gray-500">...</span>
                <button
                  type="button"
                  onClick={() => pagination.goToPage(pagination.totalPages)}
                  className="pagination-btn"
                >
                  {pagination.totalPages}
                </button>
              </>
            )}

            <button
              type="button"
              onClick={pagination.nextPage}
              disabled={!pagination.hasNext}
              className="pagination-btn"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseTable;
